import { useParams, Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { IMG_BASE } from "@/lib/constants";
import { PageLayout } from "@/components/layout/page-layout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { CodeBlock } from "@/components/ui/code-block";

const AVATAR_FALLBACK_QUERY = "&fallback=boring";
const AVATAR_VARIANT_EXAMPLE = "boring-beam";

export default function WalletPage() {
  const { address } = useParams<{ address: string }>();

  if (!address) {
    return (
      <div className="py-32 text-center">
        <div className="max-w-md mx-auto px-6">
          <h1 className="text-2xl font-extrabold tracking-tight mb-2">Missing wallet address</h1>
          <Button asChild><Link to="/"><ArrowLeft className="w-4 h-4" />Back to Home</Link></Button>
        </div>
      </div>
    );
  }

  const shortAddr = `${address.slice(0, 6)}...${address.slice(-4)}`;

  return (
    <PageLayout breadcrumbs={[{ label: "Wallet", to: "/" }, { label: shortAddr }]}>
      <section className="mb-12">
        <div className="flex flex-col sm:flex-row items-start gap-6">
          <div className="shrink-0">
            <img
              src={`${IMG_BASE}/wallet/${address}?size=256${AVATAR_FALLBACK_QUERY}`}
              alt={`Wallet ${shortAddr}`}
              className="w-32 h-32 sm:w-40 sm:h-40 rounded-2xl object-contain bg-white border shadow-sm"
              onError={(e) => { (e.target as HTMLElement).style.opacity = "0.3"; }}
            />
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">Wallet Avatar</h1>
            <p className="text-sm font-mono text-secondary-foreground mb-3 break-all">{address}</p>
            <div className="flex gap-2 flex-wrap">
              <Badge variant="secondary">Wallet</Badge>
              <Badge variant="outline">ENS Reverse Lookup</Badge>
            </div>
          </div>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">Quick Usage</h2>
        <CodeBlock language="markup" code={`<img src="${IMG_BASE}/wallet/${address}" />\n<img src="${IMG_BASE}/wallet/${address}?fallback=${AVATAR_VARIANT_EXAMPLE}" />`} />
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">How It Works</h2>
        <div className="bg-card border rounded-xl p-5 space-y-3 text-sm text-secondary-foreground">
          <p>LoadLogo performs an ENS reverse lookup on the wallet address to find an associated ENS name.</p>
          <p>If the ENS name has an avatar set, that avatar is returned. Otherwise, you can request <code>fallback=boring</code> or a specific variant like <code>fallback={AVATAR_VARIANT_EXAMPLE}</code>.</p>
        </div>
      </section>

      <section className="mb-12">
        <h2 className="text-lg font-bold tracking-tight mb-3">API Usage</h2>
        <CodeBlock language="markup" code={`<!-- Wallet avatar (ENS reverse lookup) -->\n<img src="${IMG_BASE}/wallet/${address}" />\n\n<!-- Variant-specific avatar fallback -->\n<img src="${IMG_BASE}/wallet/${address}?size=256&fallback=${AVATAR_VARIANT_EXAMPLE}" />\n\n<!-- Direct ENS lookup -->\n<img src="${IMG_BASE}/ens/{name}.eth?fallback=boring-marble" />`} />
      </section>
    </PageLayout>
  );
}
